import { useState } from "react";
import { Truck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/pages/Contact";
import { submitTransportRequest } from "@/lib/api";

const initialForm = {
    full_name: "",
    company: "",
    email: "",
    phone: "",
    origin: "",
    destination: "",
    cargo_type: "",
    weight: "",
    pickup_date: "",
    notes: "",
};

const RequestTransport = () => {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        const required = [
            "full_name",
            "email",
            "phone",
            "origin",
            "destination",
            "cargo_type",
            "weight",
        ];
        if (required.some((k) => !form[k])) {
            toast.error("Please fill out all required fields.");
            return;
        }
        setLoading(true);
        try {
            await submitTransportRequest(form);
            setDone(true);
            toast.success("Request received. Our team will be in touch.");
            setForm(initialForm);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't submit request."
            );
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid="request-transport-success"
                className="min-h-[80vh] flex items-center justify-center px-5"
            >
                <div className="max-w-xl text-center brand-border-soft rounded-3xl p-12">
                    <CheckCircle2
                        size={56}
                        className="mx-auto text-brand-green mb-6"
                    />
                    <h2 className="display text-3xl md:text-4xl text-[#1a1a1a] mb-4">
                        Request received.
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                        Thanks for reaching out. A Nexship coordinator will
                        review your load and respond shortly with carrier
                        options and pricing.
                    </p>
                    <button
                        onClick={() => setDone(false)}
                        className="btn-ghost mt-8"
                        data-testid="request-another"
                    >
                        Submit Another
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="request-transport-page" className="relative">
            <section className="relative pt-40 pb-12 md:pt-48 md:pb-16">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <div className="relative mx-auto max-w-5xl px-5 md:px-8">
                    <div className="inline-flex items-center gap-2 mono text-xs text-brand-green border border-brand-green/30 rounded-full px-3 py-1.5 mb-6 bg-brand-green/5">
                        <Truck size={14} /> NEW LOAD
                    </div>
                    <h1 className="display text-5xl md:text-7xl text-[#1a1a1a]">
                        Tell us what<br />
                        <span className="text-brand-green glow-text">
                            needs to move.
                        </span>
                    </h1>
                    <p className="mt-6 text-gray-700 max-w-xl">
                        Share the basics. We'll match the right carrier and
                        come back with pricing and timelines — usually within
                        the hour.
                    </p>
                </div>
            </section>

            <section className="relative py-16">
                <div className="mx-auto max-w-5xl px-5 md:px-8">
                    <form
                        onSubmit={onSubmit}
                        className="brand-border-soft rounded-2xl p-8 md:p-10"
                        data-testid="request-transport-form"
                    >
                        <p className="mono text-xs text-brand-green uppercase mb-6">
                            / Contact details
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label="Full name *"
                                name="full_name"
                                value={form.full_name}
                                onChange={onChange}
                                testid="rt-full-name"
                            />
                            <Field
                                label="Company"
                                name="company"
                                value={form.company}
                                onChange={onChange}
                                testid="rt-company"
                            />
                            <Field
                                label="Email *"
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={onChange}
                                testid="rt-email"
                            />
                            <Field
                                label="Phone *"
                                name="phone"
                                value={form.phone}
                                onChange={onChange}
                                testid="rt-phone"
                            />
                        </div>

                        <p className="mono text-xs text-brand-blue uppercase mt-10 mb-6">
                            / Load details
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field
                                label="Origin *"
                                name="origin"
                                value={form.origin}
                                onChange={onChange}
                                testid="rt-origin"
                                placeholder="Johannesburg, ZA"
                            />
                            <Field
                                label="Destination *"
                                name="destination"
                                value={form.destination}
                                onChange={onChange}
                                testid="rt-destination"
                                placeholder="Cape Town, ZA"
                            />
                            <Field
                                label="Cargo type *"
                                name="cargo_type"
                                value={form.cargo_type}
                                onChange={onChange}
                                testid="rt-cargo-type"
                                placeholder="Pallets / Containers / Bulk"
                            />
                            <Field
                                label="Weight *"
                                name="weight"
                                value={form.weight}
                                onChange={onChange}
                                testid="rt-weight"
                                placeholder="e.g. 12,000 kg"
                            />
                            <Field
                                label="Pickup date"
                                type="date"
                                name="pickup_date"
                                value={form.pickup_date}
                                onChange={onChange}
                                testid="rt-pickup-date"
                            />
                        </div>

                        <div className="mt-4">
                            <Field
                                label="Notes"
                                name="notes"
                                value={form.notes}
                                onChange={onChange}
                                textarea
                                testid="rt-notes"
                                placeholder="Special handling, deadlines, references..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="rt-submit"
                            className="btn-primary mt-10 disabled:opacity-60"
                        >
                            {loading ? (
                                <Loader2
                                    className="animate-spin"
                                    size={18}
                                />
                            ) : (
                                <Truck size={16} />
                            )}
                            {loading ? "Submitting..." : "Submit Request"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default RequestTransport;
